import { action, observable, computed } from 'mobx';
import * as api from '../services/api';
import { saveToLocalStorage, getFromLocalStorage } from '../services/utils';
import moment from 'moment';
import UserStore from './common/UserStore';


class PartnerManagementStore {
    @observable firstname;
    @observable lastname;
    @observable role;
    @observable outletPartnerInfo = {};
    @observable email;
    @observable googleSheetLink;
    @observable thirdPartyRiderCompany;
    @observable phoneNumber;
    @observable riderList;
    @observable riderInfo = {};
    @observable riderId = getFromLocalStorage('riderId') || null;
    
    userTypeOptions = [
        {value: 'Driver', label: 'Driver'},
        {value: 'InactiveDriver', label: 'Inactive Driver'},
        {value: 'OutletPartner', label: 'Outlet Partner'}
    ]

    // Position hierarchy: lower index = higher rank.
    // Users can only assign positions at their own level or below.
    static POSITION_RANK = {
        'Territory Manager': 0,
        'Business Manager': 1,
        'Senior Outlet Manager': 2,
        'Outlet Manager': 2,
        'Senior Assistant Outlet Manager': 2,
        'Assistant Outlet Manager': 3,
        'MIT (Existing Outlet)': 4,
        'MIT (New Outlet)': 4,
        'Outlet Partner': 5,
        'Outlet Partner (Training)': 5,
        'Hybrid Partner': 5,
        'Rider': 5,
        'Casual Worker': 5,
        'Ambassador': 5,
        'Technician': 5,
    };

    allPositionOptions = [
        {value: 'Territory Manager', label: 'Territory Manager'},
        {value: 'Business Manager', label: 'Business Manager'},
        {value: 'Senior Outlet Manager', label: 'Senior Outlet Manager'},
        {value: 'Outlet Manager', label: 'Outlet Manager'},
        {value: 'Senior Assistant Outlet Manager', label: 'Senior Assistant Outlet Manager'},
        {value: 'Assistant Outlet Manager', label: 'Assistant Outlet Manager'},
        {value: 'Outlet Partner', label: 'Outlet Partner'},
        {value: 'Outlet Partner (Training)', label: 'Outlet Partner (Training)'},
        {value: 'Hybrid Partner', label: 'Hybrid Partner'},
        {value: 'Rider', label: 'Rider'},
        {value: 'Casual Worker', label: 'Casual Worker'},
        {value: 'Ambassador', label: 'Ambassador'},
        {value: 'MIT (Existing Outlet)', label: 'MIT (Existing Outlet)'},
        {value: 'MIT (New Outlet)', label: 'MIT (New Outlet)'},
        {value: 'Technician', label: 'Technician'},
    ];

    @computed get positionOptions() {
        const currentPosition = UserStore.position;
        const currentRank = PartnerManagementStore.POSITION_RANK[currentPosition];
        // If rank unknown (e.g. admin), show all options
        if (currentRank === undefined) return this.allPositionOptions;
        return this.allPositionOptions.filter(opt => {
            const optRank = PartnerManagementStore.POSITION_RANK[opt.value];
            // Allow positions at same level or below (higher rank number)
            return optRank === undefined || optRank >= currentRank;
        });
    }

    riderRankOptions = [
        {value: 'LEADER', label: 'Leader'},
        {value: 'RIDER', label: 'Rider'}
    ];

    riderLeaderOptions = [];

    RiderSalaryOptions = [
        {value: 'FIXED_RATE', label: 'Fixed Rate'},
        {value: 'PERFORMANCE_RATE', label: 'Performance Rate'}
    ];

    RiderCashChangeOptions = [
        {value: 'OTHER', label: 'Other'},
        {value: 'COD_RETURN', label: 'COD returned'},
        {value: 'BONUS', label: 'Bonus'},
        {value: 'SALARY_PAID', label: 'Salary paid'}
    ];

    riderTypeOptions = [
        {value: 'FULL_TIME', label: 'FULL_TIME'},
        {value: 'PART_TIME', label: 'PART_TIME'},
        {value: 'THIRD_PARTY_RIDER', label: 'THIRD_PARTY_RIDER'}
    ];

    outletPartnerTypeOptions = [
        {value: 'FULL_TIME', label: 'FULL_TIME'},
        {value: 'PART_TIME', label: 'PART_TIME'}
    ];

    bankNameOptions = [
        {value: 'Affin Bank', label: 'Affin Bank'},
        { value: 'Affin Islamic Bank', label: 'Affin Islamic Bank' },
        { value: 'Agrobank', label: 'Agrobank' },
        { value: 'Al-Rajhi Banking & Investment Corporation', label: 'Al-Rajhi Banking & Investment Corporation' },
        { value: 'Alliance Bank', label: 'Alliance Bank' },
        { value: 'Alliance Islamic Bank', label: 'Alliance Islamic Bank' },
        { value: 'Ambank', label: 'Ambank' },
        { value: 'AmIslamic Bank', label: 'AmIslamic Bank' },
        { value: 'Bank Islam', label: 'Bank Islam' },
        { value: 'Bank Kerjasama Rakyat Malaysia', label: 'Bank Kerjasama Rakyat Malaysia' },
        { value: 'Bank Muamalat', label: 'Bank Muamalat' },
        { value: 'Bank of America', label: 'Bank of America' },
        { value: 'Bank of China', label: 'Bank of China' },
        { value: 'Bank of Tokyo-Mitsubishi UFJ', label: 'Bank of Tokyo-Mitsubishi UFJ' },
        { value: 'Bank Simpanan Nasional', label: 'Bank Simpanan Nasional' },
        { value: 'BNP Paribas', label: 'BNP Paribas' },
        { value: 'CIMB Bank', label: 'CIMB Bank' },
        { value: 'CIMB Islamic Bank', label: 'CIMB Islamic Bank' },
        { value: 'Citibank', label: 'Citibank' },
        { value: 'Deutsche Bank', label: 'Deutsche Bank' },
        { value: 'Hong Leong Bank', label: 'Hong Leong Bank' },
        { value: 'Hong Leong Islamic Bank', label: 'Hong Leong Islamic Bank' },
        { value: 'HSBC Bank', label: 'HSBC Bank' },
        { value: 'HSBC Amanah', label: 'HSBC Amanah' },
        { value: 'Industrial and Commercial Bank of China (ICBC)', label: 'Industrial and Commercial Bank of China (ICBC)' },
        { value: 'J.P. Morgan Chase', label: 'J.P. Morgan Chase' },
        { value: 'Kuwait Finance House', label: 'Kuwait Finance House' },
        { value: 'Maybank', label: 'Maybank' },
        { value: 'Maybank Islamic', label: 'Maybank Islamic' },
        { value: 'MBSB Bank', label: 'MBSB Bank' },
        { value: 'Mizuho Bank', label: 'Mizuho Bank' },
        { value: 'OCBC Bank', label: 'OCBC Bank' },
        { value: 'OCBC Al-Amin Bank', label: 'OCBC Al-Amin Bank' },
        { value: 'Public Bank', label: 'Public Bank' },
        { value: 'Public Islamic Bank', label: 'Public Islamic Bank' },
        { value: 'RHB Bank', label: 'RHB Bank' },
        { value: 'RHB Islamic Bank', label: 'RHB Islamic Bank' },
        { value: 'Standard Chartered Bank', label: 'Standard Chartered Bank' },
        { value: 'Standard Chartered Saadiq', label: 'Standard Chartered Saadiq' },
        { value: 'Sumitomo Mitsui Banking', label: 'Sumitomo Mitsui Banking' },
        { value: 'GXBank', label: 'GXBank' },
        { value: 'Ryt Bank', label: 'Ryt Bank' },
        { value: 'United Overseas Bank', label: 'United Overseas Bank' }
    ];


    @action setSelectedUserType = (userType) => {
        this.riderInfo.data.role = userType.value;
    };

    @action setSelectedOutletPartnerType = (outletPartnerType) => {
        this.riderInfo.data.outlet_partner_info.outlet_partner_type = outletPartnerType.value;
    };

    @action setSelectedPosition = (position) => {
        this.riderInfo.data.outlet_partner_info.position = position.value;
    };

    @action setSelectedRiderType = (riderType) => {
        this.riderInfo.data.rider_type = riderType.value;
    };

    @action setSelectedBasedInOutlet = (basedInOutlet) => {
        this.riderInfo.data.outlet = basedInOutlet.value;
    }

    @action setSelectedBankName = (bankName) => {
        this.riderInfo.data.bank_name = bankName.value;
    }

    @action setSelectedRiderRank = (riderRank) => {
        this.riderInfo.data.rank = riderRank.value;
    }

    @action setSelectedRiderLeader = (riderLeader) => {
        this.riderInfo.data.rider_leader = riderLeader.value;
    }

    @action setSelectedHubs = (selectedHubs) => {
        this.riderInfo.data.hub_codes = selectedHubs;
    }

    @action setTerminalRider = (isTerminalRider) => {
        this.riderInfo.data.terminal_rider = isTerminalRider;
    }

    @action setInsurance = (isInsured) => {
        this.riderInfo.data.insurance = isInsured;
    }

    @action
    setSelectedStatus = (status) => {
        this.selectedStatus = status;
    };

    @action
    setFirstName = (e) => {
        const { value } = e.target;
        this.riderInfo.data.firstname = value;
    };

    @action
    setLastName = (e) => {
        const { value } = e.target;
        this.riderInfo.data.lastname = value;
    };

    @action
    setEmail = (e) => {
        const { value } = e.target;
        this.riderInfo.data.email = value;
    };

    @action
    setGoogleSheetLink = (e) => {
        const { value } = e.target;
        this.riderInfo.data.google_sheet_link = value;
    };

    @action
    setThirdPartyRiderCompany = (e) => {
        const { value } = e.target;
        this.riderInfo.data.third_party_rider_company = value;
    };

    @action
    setMobileNumber = (value) => {
        this.riderInfo.data.mobileno = value;
    };

    @action
    setEmergencyContactName = (e) => {
        const { value } = e.target;
        this.riderInfo.data.emergency_contact.name = value;
    };

    @action
    setEmergencyContactNumber = (value) => {
        this.riderInfo.data.emergency_contact.mobile_no = value;
    };

    @action
    setICNumber = (value) => {
        this.riderInfo.data.ic_number = value;
    };

    @action
    setLegalName = (e) => {
        const { value } = e.target;
        this.riderInfo.data.legal_name = value;
    };

    @action
    setBankAccountNumber = (e) => {
        const { value } = e.target;
        this.riderInfo.data.bank_account_number = value;
    };

    @action
    setCompanyBankSubAccount = (e) => {
        const { value } = e.target;
        this.riderInfo.data.company_bank_sub_account = value;
    };

    @action
    setHomeAddress = (e) => {
        const { value } = e.target;
        this.riderInfo.data.home_address = value;
    };

    @action
    setBrioId = (e) => {
        const { value } = e.target;
        this.riderInfo.data.brio_id = value;
    };

    @action
    setRiderid = (id) => {
        this.riderId = id;
        saveToLocalStorage('riderId', id);
    }

    @action
    removeRiderid = () => {
        this.riderId = null;
        this.riderInfo = null;
        saveToLocalStorage('riderId', null);
    }

    @action
    setDate = (value, type) => {
        if (type == "date_joined") {
            this.riderInfo.data.outlet_partner_info.date_joined = value;
        } else if (type == "resignation_date") {
            this.riderInfo.data.outlet_partner_info.resignation_date = value;
        }
    }
    @action
    handleSavePartner = () => {
        this.loading = true;

        // Prevent saving a position higher than the current user's rank
        const targetPosition = this.riderInfo.data?.outlet_partner_info?.position;
        const currentPosition = UserStore.position;
        const currentRank = PartnerManagementStore.POSITION_RANK[currentPosition];
        const targetRank = PartnerManagementStore.POSITION_RANK[targetPosition];
        if (currentRank !== undefined && targetRank !== undefined && targetRank < currentRank) {
            this.loading = false;
            UserStore.message = `You cannot assign a position (${targetPosition}) higher than your own (${currentPosition}).`;
            return Promise.resolve('Position rank violation');
        }
        if (this.riderInfo.data.terminal_rider == null) {
            this.riderInfo.data.terminal_rider = false;
        }

        if (this.riderInfo.data.google_sheet_link != null) {
            if (!this.riderInfo.data.google_sheet_link.match(/^[a-zA-Z]+:\/\//)) {
                this.riderInfo.data.google_sheet_link = 'http://' + this.riderInfo.data.google_sheet_link;
            }
        }

        this.riderInfo.data.outlet_partner_info.date_joined = moment(this.riderInfo.data.outlet_partner_info.date_joined, "DD/MM/YYYY").valueOf();
        if (this.riderInfo.data.outlet_partner_info.resignation_date){
            this.riderInfo.data.outlet_partner_info.resignation_date = moment(this.riderInfo.data.outlet_partner_info.resignation_date, "DD/MM/YYYY").valueOf();
        }
        const dataObj = this.riderInfo.data;
        if (this.riderInfo.data.user_id) {
            return api.updateRiderInfo(dataObj).then((res_data) => {
                this.mapRiderData(res_data);
                UserStore.message = "Rider has been updated successfully.";
                return "Successfully Updated";
            })
            .catch((err) => {
                UserStore.message = "Could not add Rider or Outlet Partner, " + err.message;
                return err.message;
            })
            .finally(() => (this.loading = false));

        } else {
            return api.addRiderInfo(dataObj).then((res_data) => {
                UserStore.message = "Rider has been added successfully.";
                return "Successfully Added";
            })
            .catch((err) => {
                console.log(err)
                UserStore.message = "Could not add Rider or Outlet Partner, " + err.message;
                return err.message;
            })
            .finally(() => (this.loading = false));
        }
    }

    @action handleGetRiders = (name) => {
        this.loading = true;
        const formattedName = name && name.startsWith('0') ? `+6${name}` : name;
        const searchObj = {
            limit: 25,
            page: 1,
            sortby: 'name',
            order: 'asc',
            name: formattedName
        };
        api.getRiders(searchObj)
            .then((res) => {
                this.riderList = res.list || [];
                console.log(res)
            })
            .catch((err) => {
                console.log(err)
                this.message = err.message;
            })
            .finally(() => (this.loading = false));
    };

    @action mapRiderData = function (res_data) {
        this.riderInfo.data = res_data;
        if (res_data.rider_leader != null) {
            this.riderInfo.data.rider_leader = res_data.rider_leader.id;
        } else {
            this.riderInfo.data.rider_leader = null;
        }

        if (this.riderInfo.data.cash_balance == null) {
            this.riderInfo.data.cash_balance = 0;
        }
        if (this.riderInfo.data.outlet_partner_info == null){
            this.riderInfo.data.outlet_partner_info = {};
        } else {
            if (this.riderInfo.data.outlet_partner_info.date_joined != null){
                this.riderInfo.data.outlet_partner_info.date_joined = moment(this.riderInfo.data.outlet_partner_info.date_joined).format("DD/MM/YYYY");
            }

            if (this.riderInfo.data.outlet_partner_info.resignation_date != null){
                this.riderInfo.data.outlet_partner_info.resignation_date = moment(this.riderInfo.data.outlet_partner_info.resignation_date).format("DD/MM/YYYY");
            }
        }

        if (this.riderInfo.data.outlet_partner_info.salary == null){
            this.riderInfo.data.outlet_partner_info.salary = {};
        }

        if (this.riderInfo.data.outlet_partner_info.allowance == null){
            this.riderInfo.data.outlet_partner_info.allowance = {};
        }
        
        if (this.riderInfo.data.emergency_contact == null){
            this.riderInfo.data.emergency_contact = {};
        }

        if (!this.riderInfo.data.hub_codes) {
            this.riderInfo.data.hub_codes = [];
        }
    };


    @action fetchRiderInfo = () => {
        this.riderInfo = {};
        this.riderInfo.data = {};
        if (this.riderId) {
            api.getRiderById({ uid: this.riderId }).then((res_data) => {
                this.mapRiderData(res_data);
            })
            .catch((err) => {
                UserStore.message = err.message;
            })
            .finally(() => (this.loading = false));
        } else {
            this.riderInfo.data = {};
            this.riderInfo.data.id = null;
            this.riderInfo.data.one_time_password = '';
            this.riderInfo.data.firstname = '';
            this.riderInfo.data.lastname = '';
            this.riderInfo.data.username = '';
            this.riderInfo.data.mobileno = '';
            this.riderInfo.data.emergency_contact = {
                name: '',
                mobile_no: ''
            };
            this.riderInfo.data.role = '';
            this.riderInfo.data.outlet_partner_info = {},
            this.riderInfo.data.hub_codes = []
        }
    };

    @action doDeleteRider = (riderId) => {
        this.loading = true;
        api.deleteRiderById({ uid: riderId }).then((res_data) => {
            UserStore.message = "Record has been deleted successfully."
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        })
        .catch((err) => {
            UserStore.message = err.message;
        })
        .finally(() => {
            this.loading = false;
        });
    };
}

const store = new PartnerManagementStore();
export default store;